import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import {
  ButtonComponent,
  InputComponent,
  ModalData,
  ModalRef,
  SvgIconComponent,
  ToastService,
} from '@ui-lib/components';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScannerService } from '../scanner.service';
import { CreateScanForm } from './scanner-create-modal.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FacilitySelectComponent } from '../../../shared/components/facility-select/facility-select.component';
import { ContainerSelectorComponent } from '../../../shared/components/container-selector/container-selector.component';
import { outputError } from '../../../shared/utils/output-error.function';

@Component({
  selector: 'mon-scanner-create-modal',
  templateUrl: './scanner-create-modal.component.html',
  styleUrls: ['./scanner-create-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ScannerService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    SvgIconComponent,
    TranslateModule,
    FacilitySelectComponent,
    ContainerSelectorComponent,
  ],
})
export class ScannerCreateModalComponent extends ModalData<void> {
  private readonly scannerService = inject(ScannerService);
  private readonly modalRef = inject(ModalRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  public readonly createScanForm = this.formBuilder.group<CreateScanForm>({
    facilityId: this.formBuilder.control(null, Validators.required),
    containersId: this.formBuilder.nonNullable.control([], Validators.required),
  });

  public readonly containerSearchControl = this.formBuilder.nonNullable.control('');

  public readonly currentFacilityId = signal<string | undefined>(undefined);

  constructor() {
    super();

    this.createScanForm.controls.facilityId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.currentFacilityId.set(value?.id);
        this.createScanForm.controls.containersId.setValue([]);
        this.containerSearchControl.setValue('');
      });
  }

  public submit(): void {
    if (this.createScanForm.invalid) {
      return;
    }

    const value = this.createScanForm.getRawValue();
    const request = {
      facilityId: value.facilityId?.id ?? '',
      containersId: value.containersId,
    };

    this.scannerService
      .createScan(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (id) => this.modalRef.close(id.id),
        error: (error) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.scanner.toast.scan-creation-failed',
              {
                error: outputError(error, this.translateService.instant('errors.unknown')),
              },
            ),
          });
        },
      });
  }
}
